/**
 * Квиз-баттл — мультиплеер-режим квизов в стиле Kahoot.
 *
 * Ведущий создаёт комнату с «сборкой» (пак из 3–4 уровней), игроки заходят
 * с телефона по коду/QR, называют себя как хотят и выбирают аватар.
 * Между уровнями — экран статистики (дальше — только по кнопке ведущего),
 * после последнего уровня — супер-игра, в конце — подиум.
 */

export const QUIZ_BATTLE_MIN_PLAYERS = 1;
export const QUIZ_BATTLE_MAX_PLAYERS = 16;
export const QUIZ_BATTLE_QUESTIONS_PER_LEVEL = 8;

/** Уровень «сборки» — один квиз, обрезанный до 8 вопросов. */
export const QUIZ_LEVEL_INTRO_SECONDS = 5;
/** Сколько длится сбор согласий на супер-игру; молчуны автоматически пасуют. */
export const QUIZ_SUPER_OFFER_SECONDS = 30;

// ---------- Очки ----------
// Раунды 1–2: каждый верный ответ — 1 балл, скорость не важна.
// Раунды 3 и далее: самый быстрый верный ответ — 2 балла, остальные верные — 1.

export const QUIZ_POINTS_CORRECT = 1;
export const QUIZ_POINTS_FASTEST = 2;

/** Начиная с третьего раунда (index >= 2) быстрейший верный ответ получает больше. */
export function quizSpeedRound(levelIndex: number): boolean {
  return levelIndex >= 2;
}

export const QUIZ_AVATARS = [
  '🦊', '🐼', '🐸', '🦄', '🐙', '🦖',
  '🐯', '🐨', '🐷', '🦉', '🐺', '🦁',
  '🐵', '🦆', '🐧', '🦩', '🐢', '🐲',
  '👾', '🤖', '👻', '🎃', '😼', '🦝',
] as const;

export type QuizAvatar = (typeof QUIZ_AVATARS)[number];

export interface QuizBattleSettings {
  questionSeconds: number;
  revealSeconds: number;
}

export const DEFAULT_QUIZ_BATTLE_SETTINGS: QuizBattleSettings = {
  questionSeconds: 20,
  revealSeconds: 6,
};

// ---------- Пак уровней (контент приходит от веб-клиента ведущего) ----------

export interface QuizBattleQuestion {
  q: string;
  options: string[];
  correct: number;
  fact?: string;
}

export interface QuizBattleLevel {
  id: string;
  title: string;
  emoji: string;
  difficulty: string;
  questions: QuizBattleQuestion[];
}

export interface QuizBattlePack {
  id: string;
  title: string;
  emoji: string;
  desc: string;
  lang: 'ru' | 'en';
  levels: QuizBattleLevel[];
}

// ---------- Фазы ----------

export type QuizBattlePhase =
  | 'LOBBY'
  | 'LEVEL_INTRO'
  | 'QUESTION'
  | 'REVEAL'
  | 'LEVEL_STATS'
  | 'SUPER_OFFER'
  | 'GAME_OVER';

export type QuizPlayerStatus = 'connected' | 'disconnected';

export interface QuizPlayerPublic {
  playerId: string;
  name: string;
  avatar: string;
  status: QuizPlayerStatus;
  isCreator: boolean;
  score: number;
  /** Очки, набранные на текущем/последнем уровне. */
  levelPoints: number;
  /** Верных ответов за всю игру. */
  correctTotal: number;
  /** Отвечено вопросов за всю игру. */
  answeredTotal: number;
}

// ---------- Клиент → сервер ----------

export interface QuizClientJoin {
  type: 'quiz.join';
  name: string;
  avatar: string;
}

export interface QuizClientRejoin {
  type: 'quiz.rejoin';
  playerId: string;
}

export interface QuizClientStart {
  type: 'quiz.start';
}

export interface QuizClientAnswer {
  type: 'quiz.answer';
  levelIndex: number;
  questionIndex: number;
  optionIndex: number;
}

export interface QuizClientAdvance {
  type: 'quiz.advance';
}

export interface QuizClientSuperChoice {
  type: 'quiz.super_choice';
  /** true — игрок рискует и играет супер-игру, false — сохраняет свои баллы. */
  accept: boolean;
}

export interface QuizClientPlayAgain {
  type: 'quiz.play_again';
}

export interface QuizClientPing {
  type: 'ping';
}

export type QuizClientMessage =
  | QuizClientJoin
  | QuizClientRejoin
  | QuizClientStart
  | QuizClientAnswer
  | QuizClientAdvance
  | QuizClientSuperChoice
  | QuizClientPlayAgain
  | QuizClientPing;

// ---------- Сервер → клиент ----------

export interface QuizServerHello {
  type: 'hello';
  roomId: string;
  connectedAt: string;
}

export interface QuizServerLobby {
  type: 'quiz.lobby';
  players: QuizPlayerPublic[];
  pack: { id: string; title: string; emoji: string; desc: string; levelCount: number } | null;
  canStart: boolean;
}

export interface QuizServerPhase {
  type: 'quiz.phase';
  phase: QuizBattlePhase;
  levelIndex: number;
  questionIndex: number;
  endsAt: string | null;
}

/** Вопрос без правильного ответа — индекс приходит только в reveal. */
export interface QuizServerQuestion {
  type: 'quiz.question';
  levelIndex: number;
  questionIndex: number;
  totalInLevel: number;
  question: { q: string; options: string[] };
  endsAt: string;
  startedAt: string;
  pointsMax: number;
  /** Вопрос супер-игры: отвечают только рискнувшие, на кону все их баллы. */
  superGame?: boolean;
}

export interface QuizServerAnswered {
  type: 'quiz.answered';
  answeredCount: number;
  playerCount: number;
}

export interface QuizServerReveal {
  type: 'quiz.reveal';
  levelIndex: number;
  questionIndex: number;
  correctIndex: number;
  fact: string | null;
  /** Итог супер-игры: points — изменение счёта (может быть отрицательным). */
  superGame?: boolean;
  your: {
    answered: boolean;
    optionIndex: number | null;
    correct: boolean;
    points: number;
    score: number;
    /** Только в супер-игре: рискнул ли этот игрок. */
    super?: 'risked' | 'skipped';
  } | null;
}

export interface QuizLevelStatsRow {
  playerId: string;
  name: string;
  avatar: string;
  levelPoints: number;
  levelCorrect: number;
  levelTotal: number;
  totalScore: number;
}

export interface QuizServerLevelStats {
  type: 'quiz.level_stats';
  levelIndex: number;
  levelTitle: string;
  levelEmoji: string;
  totalLevels: number;
  isFinal: boolean;
  rows: QuizLevelStatsRow[];
}

export interface QuizSuperOfferRow {
  playerId: string;
  name: string;
  avatar: string;
  /** Баллы на кону — счёт до супер-игры. */
  score: number;
  decided: boolean;
  accepted: boolean;
}

export interface QuizServerSuperOffer {
  type: 'quiz.super_offer';
  endsAt: string;
  players: QuizSuperOfferRow[];
}

export interface QuizFinalRow {
  playerId: string;
  name: string;
  avatar: string;
  score: number;
  correctTotal: number;
  answeredTotal: number;
}

export interface QuizServerGameOver {
  type: 'quiz.game_over';
  winnerPlayerId: string | null;
  winnerName: string | null;
  rows: QuizFinalRow[];
}

export interface QuizServerReconnect {
  type: 'reconnect';
  playerId: string;
}

export interface QuizServerSessionReplaced {
  type: 'session_replaced';
  message: string;
}

export interface QuizServerError {
  type: 'error';
  code:
    | 'ROOM_NOT_FOUND'
    | 'GAME_STARTED_LATE'
    | 'NAME_TAKEN'
    | 'ROOM_FULL'
    | 'PLAYER_NOT_FOUND'
    | 'ACTION_NOT_ALLOWED'
    | 'ALREADY_ANSWERED'
    | 'NOT_IN_QUESTION'
    | 'INVALID_MESSAGE';
  message: string;
}

export interface QuizServerPong {
  type: 'pong';
  at: string;
}

export type QuizServerMessage =
  | QuizServerHello
  | QuizServerLobby
  | QuizServerPhase
  | QuizServerQuestion
  | QuizServerAnswered
  | QuizServerReveal
  | QuizServerLevelStats
  | QuizServerSuperOffer
  | QuizServerGameOver
  | QuizServerReconnect
  | QuizServerSessionReplaced
  | QuizServerError
  | QuizServerPong;

// ---------- REST ----------

export interface QuizBattleRoomSummary {
  roomId: string | null;
  code: string | null;
  joinUrl: string | null;
  qrPayload: string | null;
  state: 'lobby' | 'running' | 'finished';
  phase: QuizBattlePhase;
  levelIndex: number;
  questionIndex: number;
  pack: { id: string; title: string; emoji: string; desc: string; levelCount: number } | null;
  players: QuizPlayerPublic[];
  canStart: boolean;
  settings: QuizBattleSettings;
}
